import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { Button } from '~/components/buttons/Button/Button';
import type { NpiMatch } from '~/types';
import { MODAL_INPUT_CLASS, MODAL_LABEL_CLASS, MODAL_LINK_BTN_CLASS } from './modalStyles';

const TAGS = CT.employer.providerAssociations.inviteModal;

interface InviteProviderModalProps {
  open: boolean;
  sending: boolean;
  email: string;
  validationError: string;
  // [MCV-1894] Optional NPI; `npiMatch` is the blur lookup's answer ('none' offers Add instead).
  npi: string;
  npiError: string;
  npiMatch: NpiMatch | null;
  // [MCV-1814] A request to this email is already pending; ask whether to resend it.
  pendingExists: boolean;
  resendingPending: boolean;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onNpiChange: (npi: string) => void;
  onNpiBlur: () => void;
  onAddInstead: () => void;
  onSend: () => void;
  onResendPending: () => void;
  onCancelPending: () => void;
}

export const InviteProviderModal = ({
  open,
  sending,
  email,
  validationError,
  npi,
  npiError,
  npiMatch,
  pendingExists,
  resendingPending,
  onClose,
  onEmailChange,
  onNpiChange,
  onNpiBlur,
  onAddInstead,
  onSend,
  onResendPending,
  onCancelPending,
}: InviteProviderModalProps) => {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            data-cy={TAGS.modal}
            className="relative w-full max-w-md transform rounded-xl bg-white p-6 shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in data-closed:sm:translate-y-0 data-closed:sm:scale-95"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-content-secondary" />
                <DialogTitle className="text-lg font-semibold text-grey-100">
                  Invite Provider
                </DialogTitle>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-1.5 text-grey-400 hover:text-grey-100 hover:bg-grey-900 transition-colors"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5 text-content-secondary" />
              </button>
            </div>

            {pendingExists ? (
              <>
                <p data-cy={TAGS.pendingExistsMsg} className="mb-4 text-sm text-content-primary">
                  {`A pending request already exists for ${email.trim()}. Resend it?`}
                </p>
                <div className="mt-4 flex justify-end gap-3">
                  <Button
                    cyTag={TAGS.cancelPendingBtn}
                    type="button"
                    colorVariant="secondary"
                    onClick={onCancelPending}
                    disabled={resendingPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    cyTag={TAGS.resendPendingBtn}
                    type="button"
                    colorVariant="primary"
                    onClick={onResendPending}
                    disabled={resendingPending}
                  >
                    {resendingPending ? 'Resending...' : 'Resend'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="mb-4 text-sm text-content-secondary">
                  Enter the provider's email address. They will receive an invitation to associate
                  their vault with your organization. If they don't have an account yet, they'll be
                  asked to create one and will be associated automatically.
                </p>

                <label
                  htmlFor="invite-email"
                  className="mb-1 block text-sm font-medium text-content-primary"
                >
                  Email Address
                </label>
                <input
                  id="invite-email"
                  data-cy={TAGS.emailInput}
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onSend()}
                  placeholder="provider@example.com"
                  className={MODAL_INPUT_CLASS}
                  disabled={sending}
                />

                <label htmlFor="invite-npi" className={`${MODAL_LABEL_CLASS} mt-3`}>
                  NPI (optional)
                </label>
                <input
                  id="invite-npi"
                  data-cy={TAGS.npiInput}
                  type="text"
                  inputMode="numeric"
                  value={npi}
                  onChange={(e) => onNpiChange(e.target.value)}
                  onBlur={onNpiBlur}
                  onKeyDown={(e) => e.key === 'Enter' && onSend()}
                  placeholder="10-digit NPI"
                  className={MODAL_INPUT_CLASS}
                  disabled={sending}
                />
                {npiError && (
                  <p data-cy={TAGS.npiErrorMsg} className="mb-1 text-sm text-error">
                    {npiError}
                  </p>
                )}
                {npiMatch === 'none' && (
                  <p className="mb-1 text-sm text-content-secondary">
                    No provider found — you can{' '}
                    <button
                      type="button"
                      data-cy={TAGS.addInsteadLink}
                      className={MODAL_LINK_BTN_CLASS}
                      onClick={onAddInstead}
                    >
                      Add instead
                    </button>
                  </p>
                )}
                {validationError && (
                  <p data-cy={TAGS.errorMsg} className="mb-3 text-sm text-error">
                    {validationError}
                  </p>
                )}

                <div className="mt-4 flex justify-end gap-3">
                  <Button
                    cyTag={TAGS.cancelBtn}
                    type="button"
                    colorVariant="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    cyTag={TAGS.sendBtn}
                    type="button"
                    colorVariant="primary"
                    onClick={onSend}
                    disabled={sending}
                  >
                    {sending ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </div>
              </>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
